import React, { useEffect, useCallback, useState } from "react";
import { Grid, Row, Col, Space, Spin, message } from "antd";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import CountdownTimer from "../components/CountdownTimer";
import styles from "./StakingHome.module.css";
import _ from "lodash";
import * as Service from "../core/Service";
import dayjs from "dayjs";
import { ActionCreators } from "../redux/actions";
import { ethers } from "ethers";

const { useBreakpoint } = Grid;

const StakingHome = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const breakpoint = useBreakpoint();

  const [loading, setLoading] = useState(true);
  const [nftImg, setNftImg] = useState(null);
  const [totalNftCount, setTotalNftCount] = useState(0);
  const [stakeRecord, setStakeRecord] = useState({});
  const [expCount, setExpCount] = useState(0);
  const [ticketCount, setTicketCount] = useState(0);
  const [nextCheckInTime, setNextCheckInTime] = useState(0);
  const [withdrawing, setWithdrawing] = useState(false);

  const { IS_TESTNET } = useSelector((state) => state.app.config);

  const isPollingStakeStatus = useSelector(
    (state) => state.app.isPollingStakeStatus
  );

  const wallet_address = localStorage.getItem("wallet_address");

  const getNftCountsByAlchemy = useCallback(async () => {
    try {
      const resp = await Service.callServer("get", "/api/nft", {
        wallet_address,
      });
      setTotalNftCount(resp?.data?.count || 0);
    } catch (err) {
      console.log(err);
    }
  }, [wallet_address]);

  const getDailyCheckInData = useCallback(
    async (isPolling = false) => {
      try {
        const resp = await Service.callServer("get", "/api/stake_record", {
          wallet_address,
        });
        const data = resp.data;

        if (!resp || resp?.msg) {
          message.error(resp?.msg || "Error");
          return;
        }

        if (!_.isEmpty(data?.stake_record)) {
          const status = _.toNumber(data?.stake_record?.status);
          if (status === 1) {
            dispatch(ActionCreators.setIsPollingStakeStatus(true));
          }
          if (status === 9 || status === -1) {
            dispatch(ActionCreators.setIsPollingStakeStatus(false));
          }

          const withdraw_status = _.toNumber(
            data?.stake_record?.withdraw_status
          );
          if (withdraw_status === 1) {
            setWithdrawing(true);
          }
        }

        if (withdrawing && _.isEmpty(data?.stake_record)) {
          setWithdrawing(false);
          message.success("Genesis withdraw successful.");
        }

        setStakeRecord(data?.stake_record || {});
        setNftImg(data?.stake_record?.nft_image || null);
        setExpCount(data?.exp || 0);
        setTicketCount(data?.total_tickets || 0);
        setNextCheckInTime(
          data?.next_check_in_time > 0 ? data?.next_check_in_time : 0
        );
      } catch (err) {
        console.log(err);
      }
    },
    [wallet_address, dispatch, withdrawing]
  );

  const postCheckIn = useCallback(async () => {
    setLoading(true);

    try {
      const resp = await Service.callServer(
        "post",
        "/api/check_in_record/create",
        {
          wallet_address,
        }
      );

      if (!resp || resp?.msg) {
        message.error(resp?.msg || "Error");
        return;
      }

      if (resp?.data?.affectedRows === 2) {
        message.error("You have already checked in.");
      } else {
        message.success("Daily check-in completed, Add 1 exp.");
      }

      getDailyCheckInData();
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  }, [wallet_address, getDailyCheckInData]);

  const postWithdraw = useCallback(async () => {
    setLoading(true);

    try {
      const withdrawResp = await Service.callServer(
        "post",
        "/polygon/stake/transaction/withdraw",
        {
          wallet_address,
          token_id: _.toNumber(stakeRecord?.token_id),
        }
      );
      if (!withdrawResp || withdrawResp?.msg) {
        message.error(withdrawResp?.msg || "Error");
        return;
      }

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const withdrawSignParams = {
        from: wallet_address,
        to: withdrawResp.data.to,
        data: withdrawResp.data.data,
      };
      const signer = provider.getSigner();
      const withdrawRawTransaction = await signer.sendTransaction(
        withdrawSignParams
      );

      setWithdrawing(true);

      const resp = await Service.callServer(
        "put",
        "/api/stake_record/withdraw",
        {
          wallet_address,
        }
      );

      if (!resp || resp?.msg) {
        message.error(resp?.msg || "Error");
        return;
      }
    } catch (err) {
      console.log(err);
      message.error("Withdraw failed.", 3000);
      await Service.callServer('post', '/api/stake_record/create_error_log', {error_message: JSON.stringify(err)});
      setLoading(false);
      setWithdrawing(false);
    }

    setLoading(false);
  }, [wallet_address, stakeRecord]);

  const init = useCallback(async () => {
    if (_.isUndefined(IS_TESTNET)) {
      return;
    }
    setLoading(true);
    await getNftCountsByAlchemy();
    await getDailyCheckInData();
    setLoading(false);
  }, [IS_TESTNET, getNftCountsByAlchemy, getDailyCheckInData]);

  useEffect(() => {
    init();
  }, [init, IS_TESTNET, isPollingStakeStatus, withdrawing]);

  useEffect(() => {
    if (!isPollingStakeStatus) {
      return;
    }

    const checkStakeStatusInterval = setInterval(() => {
      getDailyCheckInData(true);
    }, 5000);

    return () => clearInterval(checkStakeStatusInterval);
  }, [isPollingStakeStatus, getDailyCheckInData]);

  useEffect(() => {
    if (!withdrawing) {
      return;
    }

    const checkStakeStatusInterval = setInterval(() => {
      getDailyCheckInData(true);
    }, 5000);

    return () => clearInterval(checkStakeStatusInterval);
  }, [withdrawing, getDailyCheckInData]);

  const getButtonText = () => {
    if (
      stakeRecord?.target_end_time >= 0 &&
      stakeRecord?.target_end_time <= dayjs().unix() &&
      _.toNumber(stakeRecord?.withdraw_status) !== 1
    ) {
      return "Withdraw Genesis";
    }
    if (!_.isEmpty(stakeRecord) && _.toNumber(stakeRecord?.status) === 9) {
      return "Genesis storing";
    }
    return "Store Genesis";
  };

  return (
    <Spin spinning={loading}>
      <AppLayout>
        <div className="boxContainer">
          <div className={styles.stakingHomeContainer}>
            <Row gutter={[12]} justify="center">
              <Col>
                <img
                  src="/images/landing/star.png"
                  style={{ width: 32, height: 32 }}
                  alt=""
                />
              </Col>
              <Col className={styles.ft_32_600_title}>Sweat & Earn</Col>
              <Col>
                <img
                  src="/images/landing/star.png"
                  style={{ width: 32, height: 32 }}
                  alt=""
                />
              </Col>
            </Row>

            <Row
              justify="center"
              className={styles.ft_44_900}
              style={{ marginTop: 16 }}
            >
              Together: we build the future of wellness
            </Row>

            <div
              className={styles.nftGradientBorder}
              style={{
                textAlign: "center",
                marginTop: breakpoint?.md ? 40 : 24,
                marginBottom: 20,
              }}
            >
              <img
                src={
                  !_.isUndefined(nftImg) && nftImg !== null
                    ? nftImg
                    : "/images/staking/sample_nft.png"
                }
                alt=""
                style={{
                  width: breakpoint?.md ? 320 : 264,
                  height: breakpoint?.md ? 320 : 264,
                  borderRadius: 24,
                }}
              />
            </div>

            <Space
              direction="vertical"
              size={20}
              style={{ display: "flex", marginBottom: 20 }}
            >
              <div>
                {nextCheckInTime > 0 && (
                  <Row justify="center" className={styles.ft_24_500}>
                    {`Daily check-in reset time: `}
                    <CountdownTimer
                      timestamp={nextCheckInTime}
                      reloadFunc={() => getDailyCheckInData()}
                    />
                  </Row>
                )}

                {stakeRecord?.target_end_time > 0 && (
                  <Row
                    justify="center"
                    className={styles.ft_24_500}
                    style={{ marginTop: nextCheckInTime > 0 ? 4 : 0 }}
                  >
                    {`Genesis storing until: `}
                    {dayjs
                      .unix(stakeRecord?.target_end_time)
                      .set("hour", 0)
                      .set("minute", 0)
                      .set("second", 0)
                      .add(1, "day")
                      .format("DD/MM/YY")}
                  </Row>
                )}
              </div>

              <Row justify="center" gutter={[20]}>
                <Col className={styles.ft_32_600}>Exp = {expCount}</Col>
                <Col className={styles.ft_32_600}>Ticket: {ticketCount}</Col>
              </Row>

              <Row justify="center" gutter={[20, 20]}>
                <Col span={breakpoint?.md ? "" : 24}>
                  <div
                    className={`${styles.button}  ${
                      ((_.isEmpty(stakeRecord) && totalNftCount <= 0) ||
                        dayjs().unix() <= nextCheckInTime || loading) &&
                      styles.buttonDisabled
                    }`}
                    onClick={() => {
                      if (
                        (_.isEmpty(stakeRecord) && totalNftCount <= 0) ||
                        dayjs().unix() <= nextCheckInTime || loading
                      ) {
                        return;
                      }
                      postCheckIn();
                    }}
                  >
                    <div className={styles.buttonText}>Daily check-in</div>
                  </div>
                </Col>
                <Col span={breakpoint?.md ? "" : 24}>
                  <div
                    className={`${styles.outlineBtn} ${
                      ((_.isEmpty(stakeRecord) && totalNftCount <= 0) ||
                        (!_.isEmpty(stakeRecord) &&
                          stakeRecord?.target_end_time > dayjs().unix()) ||
                        withdrawing ||
                        isPollingStakeStatus) &&
                      styles.outlineBtnDisabled
                    }`}
                    onClick={() => {
                      if (
                        (_.isEmpty(stakeRecord) && totalNftCount <= 0) ||
                        (!_.isEmpty(stakeRecord) &&
                          stakeRecord?.target_end_time > dayjs().unix()) ||
                        withdrawing ||
                        isPollingStakeStatus
                      ) {
                        return;
                      }
                      if (
                        !_.isEmpty(stakeRecord) &&
                        stakeRecord?.target_end_time <= dayjs().unix() &&
                        _.toNumber(stakeRecord?.withdraw_status) !== 1
                      ) {
                        postWithdraw();
                        return;
                      }
                      history.push("/storing/store");
                    }}
                  >
                    <div className={styles.outlineBtnText}>
                      {getButtonText()}
                    </div>
                  </div>
                </Col>
              </Row>

              {!loading &&
                !isPollingStakeStatus &&
                !withdrawing &&
                totalNftCount <= 0 &&
                _.isEmpty(stakeRecord) && (
                  <Row justify="center" className={styles.ft_24_500}>
                    {`You don't have Genesis NFT`}
                  </Row>
                )}

              {isPollingStakeStatus && (
                <Row justify="center" className={styles.ft_24_500}>
                  <div className={styles.loading}>Confirm Genesis storing</div>
                </Row>
              )}

              {withdrawing && (
                <Row justify="center" className={styles.ft_24_500}>
                  <div className={styles.loading}>Genesis withdrawing</div>
                </Row>
              )}
            </Space>
          </div>
        </div>
      </AppLayout>
    </Spin>
  );
};

export default StakingHome;
